import type { App } from '@hangar/shared';
import { cn } from '@/lib/utils';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { OptionSelect } from '@/components/OptionSelect';
import { RequirementRow } from './RequirementRow';
import { listView } from './envHelpers';
import { requirementKeyError } from './requirementsModel';
import { useRequirementsEditor, type RequirementsEditorApi } from './useRequirementsEditor';

/** The app's env contract (`PUT /apps/{appId}/env-requirements`): key, required, pattern, expected visibility, platforms. config_file rows are read-only. */
export function RequirementsEditor({ apps, initialAppSlug, scoped }: { apps: App[] | undefined; initialAppSlug?: string; /** One app's page (design 29): no app picker. */ scoped?: boolean }) {
  const e = useRequirementsEditor(apps, initialAppSlug);
  return (
    <div className="flex flex-col gap-4 p-4 sm:p-5" data-testid="env-requirements">
      <Toolbar e={e} apps={apps} scoped={scoped} />
      <ProblemAlert error={e.save.error} testId="env-req-problem" />
      <RequirementsBody e={e} />
    </div>
  );
}

function Toolbar({ e, apps, scoped }: { e: RequirementsEditorApi; apps: App[] | undefined; scoped?: boolean }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className={cn('flex min-w-[180px] flex-col gap-1', scoped && 'hidden')}>
        <Label htmlFor="env-req-app" className="text-xs text-subtle">
          App
        </Label>
        <OptionSelect
          id="env-req-app"
          data-testid="env-req-app"
          value={e.appSlug}
          onValueChange={e.setAppSlug}
          placeholder="Pick an app"
          options={(apps ?? []).map((a) => ({ value: a.slug, label: a.slug }))}
          className="font-mono sm:text-[13px]"
          contentClassName="font-mono"
        />
      </div>
      <p className="min-w-[24ch] flex-1 text-xs text-subtle">A build won't start while a required variable is missing or does not match its pattern.</p>
      <Button variant="outline" data-testid="env-req-add" onClick={e.add} disabled={!e.app}>
        <Icon name="add" size={18} />
        Add rule
      </Button>
      <Button data-testid="env-req-save" onClick={() => void e.onSave()} disabled={!e.dirty || !e.app} loading={e.save.isPending}>
        Save
      </Button>
    </div>
  );
}

function RequirementsBody({ e }: { e: RequirementsEditorApi }) {
  const { query, rows } = e;
  const view = listView({ isLoading: query.isLoading, isError: query.isError, hasData: Boolean(query.data), count: rows.length });
  if (view === 'loading') return <RowsSkeleton />;
  if (view === 'error') return <ErrorState compact error={query.error} onRetry={() => void query.refetch()} />;
  if (view === 'empty') return <EmptyState compact icon="rule" title="No requirements yet" description="List the variables every build of this app must have; a build won't start without them." action={<Button onClick={e.add}>Add rule</Button>} />;
  return (
    <ul className="flex flex-col gap-2">
      {rows.map((r, i) => (
        <RequirementRow key={r.id ?? `new-${i}`} r={r} i={i} keyError={requirementKeyError(rows, i, e.submitted)} update={e.update} remove={e.remove} />
      ))}
    </ul>
  );
}

function RowsSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
